/**
 * i18next setup.
 *
 * Language is determined by the URL prefix (`/cs/...`, `/en/...`) — this is
 * the SEO-friendly approach. `i18n.changeLanguage` is called from the router
 * whenever the `:lang` segment changes; no separate LanguageDetector plugin
 * is wired up for the URL because the router owns that state.
 *
 * Namespaces are split per domain (common / quote / materials / ...) so FE
 * code loads only the strings it needs and bundles stay small.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  editModeStore,
  encodeMarkerId,
  isEditModeActive,
  MARKER_CLOSE,
  MARKER_OPEN,
} from "@/components/edit/editModeStore";
import {
  EDITABLE_NAMESPACES,
  isLockedI18nKey,
  isPlatformEditableKey,
  isPlatformLockedI18nKey,
  PLATFORM_EDITABLE_NAMESPACES,
} from "@/i18n/editableNamespaces";
import { isActiveLang } from "@/i18n/languageRegistry";
import { resolveShell } from "@/shell/resolveShell";


// The compiled-in (builtin) languages — always available synchronously,
// always the i18next fallback. The *routable* set is broader at runtime:
// a platform admin can enable more locales, delivered via the branding
// payload and tracked in ``languageRegistry``. ``SupportedLang`` stays the
// builtin union for code that is genuinely CS/EN-typed; runtime code that
// must accept admin-added locales goes through ``isSupportedLang`` (which
// consults the registry) and treats the value as a plain string.
export const SUPPORTED_LANGS = ["cs", "en"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];
export const DEFAULT_LANG: SupportedLang = "cs";

/**
 * Is ``value`` a routable ``/:lang`` segment? Delegates to the runtime
 * language registry (builtins ∪ admin-enabled locales), so every
 * ``isSupportedLang(param) ? param : DEFAULT_LANG`` gate across the app
 * honours a language the platform admin added — no per-callsite change.
 * The ``value is SupportedLang`` narrowing is a deliberate convenience:
 * downstream code only ever uses the value as an i18n language *string*.
 */
export function isSupportedLang(value: unknown): value is SupportedLang {
  return isActiveLang(value);
}

/**
 * Texty se do instance NEregistrují tady (KAN-223). Tenhle modul importuje
 * přes 500 souborů kvůli ``isSupportedLang``/``DEFAULT_LANG``, takže
 * cokoli, co by tu viselo staticky, jde do KAŽDÉHO vstupního bodu — včetně
 * ``/embed`` na cizím webu, který tak stahoval celý ``admin.json`` (~350 kB
 * gzip), i když ho nikdy nevykreslí.
 *
 * Kdo texty registruje:
 * - aplikace (``main.tsx``) a testy (``tests/setup.ts``) — ``@/i18n/resources``,
 *   všechny jmenné prostory, jako dřív;
 * - ``/embed`` (``embed/main.tsx``) — ``@/i18n/embedResources``, jen
 *   zákaznickou podmnožinu, zbytek až na vyžádání.
 */

/**
 * Edit-mode marker token — re-exported from ``editModeStore`` for the
 * DOM observer that consumes them.
 *
 * The wrapping format uses *only* zero-width / invisible Unicode chars
 * (U+2063 INVISIBLE SEPARATOR + U+200B ZERO WIDTH SPACE + U+200C ZERO
 * WIDTH NON-JOINER) so a misbehaving font cannot leak any human-visible
 * artefact into the rendered page. The (ns, key) lookup happens via the
 * editModeStore hit registry — the marker only carries a numeric id.
 */
export {
  MARKER_OPEN as EDIT_TOKEN_OPEN,
  MARKER_CLOSE as EDIT_TOKEN_CLOSE,
  MARKER_RE as EDIT_TOKEN_RE,
} from "@/components/edit/editModeStore";

interface EditableMarkerOptions {
  ns?: string | string[];
  lng?: string;
  [key: string]: unknown;
}

i18n.use({
  type: "postProcessor",
  name: "editableMarker",
  process(
    value: unknown,
    key: string | string[],
    options: EditableMarkerOptions,
  ): unknown {
    if (!isEditModeActive()) return value;
    if (typeof value !== "string") return value;
    if (value.includes(MARKER_OPEN)) return value;  // already wrapped
    const ns = Array.isArray(options.ns)
      ? options.ns[0]
      : options.ns ?? "common";
    // The editable namespace set is per-shell: the platform admin also
    // owns ``saas`` (the acquisition funnel), which no tenant route
    // renders. Reading the tenant set on the platform shell would have
    // silently skipped every pencil on ``/for-providers`` even though
    // the backend accepts the write.
    const isPlatformShell = resolveShell() === "platform";
    const editableNamespaces = isPlatformShell
      ? PLATFORM_EDITABLE_NAMESPACES
      : EDITABLE_NAMESPACES;
    if (!editableNamespaces.has(ns)) return value;
    const flatKey = Array.isArray(key) ? key[0] : key;
    // Promise-bearing keys are rendered from landing_facts; the pencil
    // would let a tenant claim numbers that aren't in their BE config.
    if (isLockedI18nKey(ns, flatKey)) return value;
    // Within an editable namespace the platform shell persists only
    // whitelisted key prefixes — skip pencils on Header/Footer/etc. keys
    // that would 400 on save. (No such restriction on the tenant shell.)
    if (isPlatformShell) {
      if (!isPlatformEditableKey(ns, flatKey)) return value;
      // Hard platform commitments (trial days, instant-quote seconds, …)
      // spell their numbers out in the copy. Free-edit would let staff
      // overwrite "14 days free" to a number the platform doesn’t
      // actually offer.
      if (isPlatformLockedI18nKey(ns, flatKey)) return value;
    }
    // Peek the raw resource template (pre-interpolation). If it
    // contains ``{{...}}`` we can't safely round-trip the resolved
    // value into an override, so skip wrapping. By the time the
    // post-processor runs the ``value`` is already interpolated.
    const lng =
      typeof options.lng === "string" ? options.lng : i18n.language;
    const raw = i18n.getResource(lng, ns, flatKey);
    if (typeof raw === "string" && /\{\{.+?\}\}/.test(raw)) return value;
    const id = editModeStore.registerHit(ns, flatKey);
    return `${MARKER_OPEN}${encodeMarkerId(id)}${MARKER_OPEN}${value}${MARKER_CLOSE}`;
  },
} as Parameters<typeof i18n.use>[0]);

/**
 * Jazyk z URL (``/:lang/…``) už při startu (KAN-1642). Start na
 * ``DEFAULT_LANG`` znamenal, že první render na ``/en`` poslal dotazy
 * (``/public/branding?lang=cs``, …) v češtině a ``App`` je hned po přepnutí
 * jazyka všechny zneplatnil a poslal znovu. ``App`` zůstává strážcem:
 * nepodporovaný segment tu jen propadne na výchozí jazyk.
 */
function initialLang(): string {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const segment = window.location.pathname.split("/")[1] ?? "";
  return (SUPPORTED_LANGS as readonly string[]).includes(segment)
    ? segment
    : DEFAULT_LANG;
}

void i18n.use(initReactI18next).init({
  // Prázdné, ne vynechané: s ``resources`` v options i18next ví, že žádný
  // backend není, a ``hasLoadedNamespace`` hlásí hotovo — texty přidá
  // vstupní bod přes ``addResourceBundle`` (viz výš).
  resources: {},
  lng: initialLang(),
  fallbackLng: DEFAULT_LANG,
  defaultNS: "common",
  ns: [
    "common",
    "quote",
    "cart",
    "checkout",
    "order",
    "admin",
    "legal",
    "account",
    "auth",
    "buyer",
    "track",
    "contact",
    "materials",
    "pricing",
    "public",
    "how_it_works",
    "saas",
  ],
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnNull: false,
  postProcess: ["editableMarker"],
});

/** Texty jednoho vstupního bodu: ``{ jazyk: { namespace: json } }``. */
export type I18nBundles = Record<string, Record<string, object>>;

/**
 * Zaregistruj vestavěné texty (viz ``resources.ts`` / ``embedResources.ts``).
 *
 * ``deep`` ano, ``overwrite`` NE: namespace, který dorazí až po startu
 * (``/embed`` ho dotahuje na vyžádání), nesmí přepsat úpravu textu, kterou
 * mezitím nanesl tenant (``useShellI18nOverrides``) — ta má přednost.
 */
export function registerBundles(bundles: I18nBundles): void {
  for (const [lng, namespaces] of Object.entries(bundles)) {
    for (const [ns, data] of Object.entries(namespaces)) {
      i18n.addResourceBundle(lng, ns, data, true, false);
    }
  }
}

export default i18n;
